import { RoomGroups } from './types';
import { PropertyField, RoomField } from '../../../../system/field';
import {
  LevelType,
  normalizeLevel,
  Room,
  RoomGroupType,
  RoomSummary,
} from '../../../../system/rooms';
import { findNextPositiveNumberFromArray } from '../../../../utils';
export { arrayMove } from '@dnd-kit/sortable';

export const getRoomsCounter = (
  rooms: Room[],
  roomGroups: RoomGroupType[] = RoomGroups,
): RoomGroupType[] => {
  const roomTypesCounter = rooms.reduce((acc: any, room: Room) => {
    const roomType = room[RoomField.ROOM_TYPE].toLowerCase();

    roomGroups.forEach((group) => {
      const matches = [group.type, ...(group.groupWith || [])].filter((g) => {
        const groupType = g.toLowerCase();
        // For short abbreviations (e.g., "bed", "bath"), check if group type starts with it
        // For longer types (e.g., "Bedroom 1", "Half Bath"), check if it contains the group type
        if (roomType.length <= 4) {
          return groupType.startsWith(roomType);
        }
        return roomType.includes(groupType);
      });
      if (matches.length) {
        acc[group.type] = acc[group.type] ? acc[group.type] + 1 : 1;
      }
    });

    return acc;
  }, {});

  return roomGroups.map((group) => {
    return { ...group, count: roomTypesCounter[group.type] ?? 0 };
  });
};

export const getNextItemNumber = (existingRooms: Room[]): number => {
  if (!existingRooms.length) return 1;

  const maxItemNumber = Math.max(
    ...existingRooms.map((room) => Number(room.ROOM_ITEM_NUMBER || 0)),
  );

  return maxItemNumber + 1;
};

export const matchesCounteredRoomType = (roomType: string) => {
  const matches = roomType.match(/^(bedroom|bathroom)(\s*(\d+))?/i);
  return matches;
};

export const normalizeRoomType = (
  roomType: string,
): { type: string; number?: number; isNumberedRoomType: boolean } => {
  const matches = matchesCounteredRoomType(roomType);
  if (!matches) return { type: roomType, isNumberedRoomType: false };

  const typeString = matches[1]; // "bedroom" or "bathroom"

  return {
    type: typeString,
    number: matches[2] ? parseInt(matches[2]) : 0,
    isNumberedRoomType: true,
  };
};

export interface RoomSummaryUpdateOptions {
  hasLaundryFeature?: boolean;
  hasSimpleCountMode?: boolean;
  hasUserEnteredCounts?: boolean;
}

export const getRoomSummaryUpdates = (
  summary: RoomSummary,
  options: RoomSummaryUpdateOptions = {},
) => {
  const {
    hasLaundryFeature = false,
    hasSimpleCountMode = false,
    hasUserEnteredCounts = false,
  } = options;

  // Under the user-entered-counts flag, STORY_LIST is owned by addEmptyLevel
  // / deleteEmptyLevels (and preserved as-is from the listing otherwise).
  // Writing it here would stomp legacy/user-added levels that don't have
  // matching Room resources, and the SET rules would then clear the per-level
  // values tied to those missing stories.
  let updates: any = hasUserEnteredCounts
    ? {}
    : {
        [PropertyField.STORY_LIST]: summary.storyList,
      };

  if (!hasUserEnteredCounts) {
    // Main level per-level counts are user-entered under the flag; skip.
    updates[PropertyField.MAIN_LEVEL_BEDROOMS] = summary.mainLevel.bedrooms;
    updates[PropertyField.MAIN_LEVEL_FULL_BATHS] = summary.mainLevel.fullBaths;
    updates[PropertyField.MAIN_LEVEL_HALF_BATHS] = summary.mainLevel.halfBaths;
  }

  if (hasUserEnteredCounts) {
    // Per-level and totals are user-entered / derived elsewhere. Only emit
    // story list + laundry (handled below).
  } else if (hasSimpleCountMode) {
    // Simple counting mode: only update aggregate fields, skip per-index loops
    updates[PropertyField.FULL_BATHS_LOWER] = summary.fullBathsLower;
    updates[PropertyField.FULL_BATHS_MAIN] = summary.fullBathsMain;
    updates[PropertyField.FULL_BATHS_UPPER] = summary.fullBathsUpper;

    updates[PropertyField.BEDS_ON_MAIN] = summary.mainLevel.bedrooms;
  } else {
    // Standard mode: update per-index fields
    // Add all upper level fields
    for (let i = 1; i <= 6; i++) {
      const upperCounts = summary.upperLevels[i];

      const update =
        upperCounts.hasRoom ||
        upperCounts.bedrooms > 0 ||
        upperCounts.fullBaths > 0 ||
        upperCounts.halfBaths > 0;
      if (!update) {
        updates[`UPPER_LEVEL_${i}_BEDROOMS` as PropertyField] = null;
        updates[`UPPER_LEVEL_${i}_FULL_BATHS` as PropertyField] = null;
        updates[`UPPER_LEVEL_${i}_HALF_BATHS` as PropertyField] = null;
        continue;
      }

      updates[`UPPER_LEVEL_${i}_BEDROOMS` as PropertyField] =
        upperCounts.bedrooms;
      updates[`UPPER_LEVEL_${i}_FULL_BATHS` as PropertyField] =
        upperCounts.fullBaths;
      updates[`UPPER_LEVEL_${i}_HALF_BATHS` as PropertyField] =
        upperCounts.halfBaths;
    }

    // Add all lower level fields
    for (let i = 1; i <= 6; i++) {
      const lowerCounts = summary.lowerLevels[i];

      const update =
        lowerCounts.hasRoom ||
        lowerCounts.bedrooms > 0 ||
        lowerCounts.fullBaths > 0 ||
        lowerCounts.halfBaths > 0;
      if (!update) {
        updates[`LOWER_LEVEL_${i}_BEDROOMS` as PropertyField] = null;
        updates[`LOWER_LEVEL_${i}_FULL_BATHS` as PropertyField] = null;
        updates[`LOWER_LEVEL_${i}_HALF_BATHS` as PropertyField] = null;
        continue;
      }

      updates[`LOWER_LEVEL_${i}_BEDROOMS` as PropertyField] =
        lowerCounts.bedrooms;
      updates[`LOWER_LEVEL_${i}_FULL_BATHS` as PropertyField] =
        lowerCounts.fullBaths;
      updates[`LOWER_LEVEL_${i}_HALF_BATHS` as PropertyField] =
        lowerCounts.halfBaths;
    }
  }

  if (!hasUserEnteredCounts) {
    // set totals last
    updates = {
      ...updates,
      [PropertyField.BEDROOMS_TOTAL]: summary.totalBedrooms,
      [PropertyField.BATHROOMS_FULL]: summary.totalFullBaths,
      [PropertyField.ROOMS_TOTAL]: summary.totalRooms,
    };

    if (!hasSimpleCountMode) {
      // Only auto-set half bath totals when room types include half bath
      updates[PropertyField.BATHROOMS_HALF] = summary.totalHalfBaths;
      updates[PropertyField.BATHROOMS_TOTAL_INTEGER] = summary.totalBathrooms;
    }
  }

  // Add laundry fields if feature is enabled
  if (hasLaundryFeature) {
    updates[PropertyField.LAUNDRY_LOWER] = summary.laundryLower;
    updates[PropertyField.LAUNDRY_ON_MAIN] = summary.laundryMain;
    updates[PropertyField.LAUNDRY_UPPER] = summary.laundryUpper;
    updates[PropertyField.LAUNDRY_TOTAL] = summary.totalLaundry;
  }

  return updates;
};

export interface PerLevelFieldValues {
  mainBedrooms: number;
  mainFullBaths: number;
  mainHalfBaths: number;
  upperBedrooms: number[];
  upperFullBaths: number[];
  upperHalfBaths: number[];
  lowerBedrooms: number[];
  lowerFullBaths: number[];
  lowerHalfBaths: number[];
}

export interface PerLevelTotals {
  [PropertyField.BEDROOMS_TOTAL]: number;
  [PropertyField.BATHROOMS_FULL]: number;
  [PropertyField.BATHROOMS_HALF]: number;
  [PropertyField.BATHROOMS_TOTAL_INTEGER]: number;
}

const sum = (values: number[]) =>
  values.reduce((acc, v) => acc + (Number(v) || 0), 0);

export const getTotalsFromPerLevelFields = (
  values: PerLevelFieldValues,
): PerLevelTotals => {
  const totalBedrooms =
    (Number(values.mainBedrooms) || 0) +
    sum(values.upperBedrooms) +
    sum(values.lowerBedrooms);
  const totalFullBaths =
    (Number(values.mainFullBaths) || 0) +
    sum(values.upperFullBaths) +
    sum(values.lowerFullBaths);
  const totalHalfBaths =
    (Number(values.mainHalfBaths) || 0) +
    sum(values.upperHalfBaths) +
    sum(values.lowerHalfBaths);

  return {
    [PropertyField.BEDROOMS_TOTAL]: totalBedrooms,
    [PropertyField.BATHROOMS_FULL]: totalFullBaths,
    [PropertyField.BATHROOMS_HALF]: totalHalfBaths,
    [PropertyField.BATHROOMS_TOTAL_INTEGER]: totalFullBaths + totalHalfBaths,
  };
};

export const getNextLevel = (
  rooms: Room[],
  emptyLevels: string[],
  levelType: LevelType,
): number => {
  const uniqueLevels = new Set<number>();

  const addIfValidLevel = (levelStr: string) => {
    const level = normalizeLevel(levelStr);
    if (
      levelStr &&
      levelStr !== LevelType.UNSPECIFIED &&
      level.type.toLowerCase() === levelType.toLowerCase() &&
      level.number !== undefined
    ) {
      uniqueLevels.add(level.number);
    }
  };

  // Process room levels
  rooms.forEach((room: Room) => {
    addIfValidLevel(room.ROOM_LEVEL || '');
  });

  // Process empty levels
  emptyLevels.forEach(addIfValidLevel);

  return findNextPositiveNumberFromArray(Array.from(uniqueLevels));
};

/**
 * Check if all levels are expanded
 */
export const areAllLevelsExpanded = (
  expandedLevels: Record<string, boolean>,
): boolean => {
  return Object.values(expandedLevels).some((value) => value === true);
};

/**
 * Check if all levels are expanded
 */
export const checkExpandableLevels = (
  expandedLevels: Record<string, boolean>,
): boolean => {
  return Object.values(expandedLevels).some((value) => value === false);
};

export const getEmptyLevelKey = (
  level: LevelType,
  nextLevel: number,
  suffix: string = 'TOTAL_BATHS', // TOTAL_BATHS, HALF_BATHS, FULL_BATHS, BEDROOMS
): string => {
  return `${level.toUpperCase()}_LEVEL_${nextLevel}_${suffix}`;
};

export const getEmptyLevelKeys = (
  level: LevelType,
  nextLevel: number,
  value: number | null,
): Record<string, number | null> => {
  const halfBathsKey = getEmptyLevelKey(level, nextLevel, 'HALF_BATHS');
  const fullBathsKey = getEmptyLevelKey(level, nextLevel, 'FULL_BATHS');
  const bedroomsKey = getEmptyLevelKey(level, nextLevel, 'BEDROOMS');

  return {
    [halfBathsKey]: value,
    [fullBathsKey]: value,
    [bedroomsKey]: value,
  };
};

export const generateLevels = (levelType: LevelType, maxLevel: number) => {
  return Array.from({ length: maxLevel }, (_, i) => i + 1).map((i) => {
    return `${levelType} ${i}`;
  });
};

export const getMaxLevels = (levels: string[]) => {
  let upperMaxLevel = 0;
  let lowerMaxLevel = 0;
  levels.forEach((level) => {
    const normalizedLevel = normalizeLevel(level);
    if (normalizedLevel.type === LevelType.UPPER && normalizedLevel.number) {
      upperMaxLevel = Math.max(normalizedLevel.number, upperMaxLevel);
    }

    if (normalizedLevel.type === LevelType.LOWER && normalizedLevel.number) {
      lowerMaxLevel = Math.max(normalizedLevel.number, lowerMaxLevel);
    }
  });

  return {
    upperMaxLevel,
    lowerMaxLevel,
  };
};

export const getMaxRoomType = (
  roomType: string,
  roomTypes: string[] = [],
): string => {
  const normalizedRoomType = normalizeRoomType(roomType);
  const isNumberedRoomType = normalizedRoomType.isNumberedRoomType;

  if (!isNumberedRoomType) {
    return roomType;
  }

  const filteredRoomNumbers = roomTypes.reduce(
    (roomNumbers, localRoomType): number[] => {
      if (!localRoomType.includes(normalizedRoomType.type)) {
        return roomNumbers;
      }

      const _normalizedType = normalizeRoomType(localRoomType);
      const roomNumber = _normalizedType.number;

      if (roomNumber && roomNumber >= 1 && !roomNumbers.includes(roomNumber)) {
        return [...roomNumbers, roomNumber];
      }

      return roomNumbers;
    },
    [] as number[],
  );

  const nextRoomType = findNextPositiveNumberFromArray(filteredRoomNumbers);

  // special case for Bathroom since there is no generic type
  if (normalizedRoomType.type === 'Bathroom' && nextRoomType >= 4) {
    return 'Full Bath';
  }

  return `${normalizedRoomType.type} ${nextRoomType}`;
};
